import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { useOnboardingState } from "@/hooks/useOnboardingState";
import { SalesStep } from "@/components/onboarding/SalesStep";
import { DesignStep, DESIGN_VIEWS, type DesignView } from "@/components/onboarding/DesignStep";
import { ApprovalsStep } from "@/components/onboarding/ApprovalsStep";
import { SummaryStep } from "@/components/onboarding/SummaryStep";
import { IndustrySelectStep } from "@/components/onboarding/IndustrySelectStep";
import { SitePlanStep } from "@/components/onboarding/SitePlanStep";
import { FloorElectricalStep } from "@/components/onboarding/FloorElectricalStep";
import { StructuralSectionStep } from "@/components/onboarding/StructuralSectionStep";
import { ArchitecturalStep } from "@/components/onboarding/ArchitecturalStep";
import { HomebuilderApprovalsStep } from "@/components/onboarding/HomebuilderApprovalsStep";
import { InviteTeamStep } from "@/components/onboarding/InviteTeamStep";
import { SelectionSummaryBar } from "@/components/onboarding/SelectionSummaryBar";
import { IntegrationRequestDialog } from "@/components/IntegrationRequestDialog";
import { RotateCcw, Home } from "lucide-react";
import type { Industry } from "@/data/personaPresets";
import { ONBOARDING_STAGES } from "@/data/onboardingData";
import { HOMEBUILDER_STAGES } from "@/data/homebuilderData";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const STEP_TO_STAGE_KEYS: Record<string, string[]> = (() => {
  const map: Record<string, string[]> = {};
  ONBOARDING_STAGES.forEach(s => { map[s.id] = s.dataPoints.map(dp => dp.key); });
  HOMEBUILDER_STAGES.forEach(s => {
    const stepId = s.id === "hb-approvals" ? "hb-approvals" : s.id.replace(/^hb-/, "");
    map[stepId] = s.dataPoints.map(dp => dp.key);
  });
  return map;
})();

// Per-subview keys for the Design step so typical points are added one tab at a time.
const DESIGN_SUBVIEW_KEYS: Record<DesignView, string[]> = (() => {
  const stage = ONBOARDING_STAGES.find(s => s.id === "design");
  const electrical = new Set(stage?.subsections?.find(s => s.label === "Electrical")?.keys || []);
  const structural = new Set(stage?.subsections?.find(s => s.label === "Structural")?.keys || []);
  const all = stage?.dataPoints.map(dp => dp.key) || [];
  return {
    solar: all.filter(k => !electrical.has(k) && !structural.has(k)),
    electrical: all.filter(k => electrical.has(k)),
    structural: all.filter(k => structural.has(k)),
  };
})();

type Step = "industry" | "sales" | "design" | "approvals" | "summary" | "invite" | "site-plan" | "floor-electrical" | "structural-section" | "architectural" | "hb-approvals";
const ALL_STEPS_DEFAULT: Step[] = ["industry", "sales", "design", "approvals", "summary"];
const ALL_STEPS_HOMEBUILDER: Step[] = ["industry", "site-plan", "floor-electrical", "structural-section", "architectural", "hb-approvals", "summary"];
const DEMO_STEPS: Step[] = ["sales", "design", "approvals"];

const stepLabels: Record<Step, string> = {
  industry: "Industry",
  sales: "Sales",
  design: "Design & Engineering",
  approvals: "Approvals",
  summary: "See Your Data",
  invite: "Invite Your Team",
  "site-plan": "Site Plan",
  "floor-electrical": "Floor & Electrical Plan",
  "structural-section": "Structural Section Plan",
  architectural: "Architectural Plan",
  "hb-approvals": "Permitting",
};

const STORAGE_KEY = "fordje-onboarding-selections";

export default function Onboarding() {
  const [searchParams] = useSearchParams();
  const mode = searchParams.get("mode");
  const isDemo = mode === "demo";
  const [selectedIndustry, setSelectedIndustry] = useState<Industry | null>(null);

  const ALL_STEPS: Step[] = selectedIndustry?.id === "residential-builder"
    ? ALL_STEPS_HOMEBUILDER
    : ALL_STEPS_DEFAULT;

  const STEPS = isDemo ? DEMO_STEPS : ALL_STEPS;

  const [step, setStep] = useState<Step>(STEPS[0]);
  const [designView, setDesignView] = useState<DesignView>("solar");
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const navigate = useNavigate();
  const { selectedKeys, toggle, count, persist, getSelectedLabels, reinitialize, ensureTypicalForStage } = useOnboardingState(isDemo);

  useEffect(() => {
    if (step === "design") {
      ensureTypicalForStage(`design:${designView}`, DESIGN_SUBVIEW_KEYS[designView]);
      return;
    }
    const stageKeys = STEP_TO_STAGE_KEYS[step];
    if (stageKeys) ensureTypicalForStage(step, stageKeys);
  }, [step, designView, ensureTypicalForStage]);

  useEffect(() => {
    if (isDemo) localStorage.removeItem(STORAGE_KEY);
  }, [isDemo]);

  const stepIndex = STEPS.indexOf(step);
  const progress = ((stepIndex) / (STEPS.length - 1)) * 100;

  // Push a step-level event to GTM/GA on every Try-It step (and design subview) change
  // so we can build a funnel and see drop-off across the flow.
  useEffect(() => {
    const w = window as unknown as { dataLayer?: Array<Record<string, unknown>> };
    w.dataLayer = w.dataLayer || [];
    const stepName = step === "design" ? `design:${designView}` : step;
    w.dataLayer.push({
      event: "try_it_step",
      try_it_step_name: stepName,
      try_it_step_index: stepIndex + 1,
      try_it_total_steps: STEPS.length,
      try_it_industry: selectedIndustry?.id ?? null,
      try_it_mode: isDemo ? "demo" : "standard",
    });
  }, [step, designView, stepIndex, STEPS.length, selectedIndustry?.id, isDemo]);



  const goNext = () => {
    if (step === "design") {
      const idx = DESIGN_VIEWS.indexOf(designView);
      if (idx < DESIGN_VIEWS.length - 1) { setDesignView(DESIGN_VIEWS[idx + 1]); return; }
    }
    if (stepIndex < STEPS.length - 1) setStep(STEPS[stepIndex + 1]);
  };
  const goBack = () => {
    if (step === "design") {
      const idx = DESIGN_VIEWS.indexOf(designView);
      if (idx > 0) { setDesignView(DESIGN_VIEWS[idx - 1]); return; }
    }
    if (stepIndex > 0) setStep(STEPS[stepIndex - 1]);
  };

  const isLastStep = stepIndex === STEPS.length - 1;

  const handleComplete = () => {
    persist();
    navigate("/request-demo");
  };

  const handleResetDemo = () => {
    localStorage.removeItem(STORAGE_KEY);
    window.location.href = `/try-it?mode=${mode}`;
  };

  const handleIndustrySelect = (industry: Industry) => {
    setSelectedIndustry(industry);
    reinitialize(industry.id);
    const nextSteps: Step[] = industry.id === "residential-builder" ? ALL_STEPS_HOMEBUILDER : ALL_STEPS_DEFAULT;
    const idx = nextSteps.indexOf("industry");
    setStep(nextSteps[idx + 1]);
  };

  const showSummaryBar = step === "invite"
    ? false
    : isDemo
      ? step !== "industry"
      : step !== "industry" && step !== "summary";

  const designViewIdx = DESIGN_VIEWS.indexOf(designView);
  const designLabels: Record<DesignView, string> = { solar: "Solar", electrical: "Electrical", structural: "Structural" };
  const nextLabel = isDemo && isLastStep
    ? "See Your Data →"
    : step === "design" && designViewIdx < DESIGN_VIEWS.length - 1
      ? `Next: ${designLabels[DESIGN_VIEWS[designViewIdx + 1]]} →`
      : (step === "approvals" || step === "hb-approvals")
        ? "See Your Data"
        : "Next";

  const handleNextClick = () => {
    if (isDemo && isLastStep) handleComplete();
    else goNext();
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <div className="sticky top-0 z-40 bg-background border-b">
        <div className="max-w-6xl mx-auto px-6 py-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex flex-col leading-tight">
              <span className="text-xs font-semibold uppercase tracking-widest text-fordje-blue">Try It</span>
              <span className="text-xs text-muted-foreground">
                Step {stepIndex + 1} of {STEPS.length}
              </span>
            </div>

            <div className="flex items-center gap-3">
              {isDemo && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button>
                      <Badge variant="outline" className="cursor-pointer text-xs bg-accent/50 hover:bg-accent">Demo Mode</Badge>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={handleResetDemo}>
                      <RotateCcw className="h-3.5 w-3.5 mr-2" />Reset
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              <Link
                to="/"
                className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                title="Exit to Home"
              >
                <Home className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Exit</span>
              </Link>
              <span className="h-5 w-px bg-border" />
              <Link to="/" className="flex items-center gap-2 shrink-0 group" title="Back to Fordje home">
                <img
                  src="/lovable-uploads/595a50f5-c631-4403-8464-cad23f811cfe.png"
                  alt="Fordje"
                  className="h-6 w-6"
                />
                <span className="text-base font-bold font-ubuntu text-foreground group-hover:text-fordje-blue transition-colors">
                  Fordje
                </span>
              </Link>
            </div>
          </div>

          {/* Full step list — visible at all times so users see what's ahead */}
          <ol className="hidden md:flex items-center gap-2 mb-2 overflow-x-auto">
            {STEPS.map((s, i) => {
              const state = i < stepIndex ? "done" : i === stepIndex ? "current" : "upcoming";
              const clickable = i < stepIndex;
              const Tag: "button" | "span" = clickable ? "button" : "span";
              return (
                <li key={s} className="flex items-center gap-2 shrink-0">
                  <Tag
                    {...(clickable
                      ? {
                          type: "button" as const,
                          onClick: () => {
                            if (step === "design") setDesignView("solar");
                            setStep(STEPS[i]);
                          },
                          title: `Go back to ${stepLabels[s]}`,
                        }
                      : {})}
                    className={
                      "flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium transition-colors " +
                      (state === "current"
                        ? "bg-primary/10 text-primary ring-1 ring-primary/30"
                        : state === "done"
                          ? "text-foreground/80 hover:bg-muted hover:text-foreground cursor-pointer"
                          : "text-muted-foreground")
                    }
                  >
                    <span
                      className={
                        "flex h-4 w-4 items-center justify-center rounded-full text-[10px] font-semibold " +
                        (state === "current"
                          ? "bg-primary text-primary-foreground"
                          : state === "done"
                            ? "bg-primary/80 text-primary-foreground"
                            : "bg-muted text-muted-foreground")
                      }
                    >
                      {i + 1}
                    </span>
                    {stepLabels[s]}
                  </Tag>
                  {i < STEPS.length - 1 && (
                    <span className={`h-px w-4 ${i < stepIndex ? "bg-primary/60" : "bg-border"}`} />
                  )}
                </li>
              );
            })}
          </ol>

          {/* Mobile: current label + dot strip */}
          <div className="md:hidden flex items-center justify-between gap-3 mb-2">
            <span className="text-sm font-semibold truncate">{stepLabels[step]}</span>
            <div className="flex gap-1 shrink-0">
              {STEPS.map((s, i) => (
                <span key={s} className={`h-1.5 w-6 rounded-full transition-colors ${i <= stepIndex ? "bg-primary" : "bg-muted"}`} />
              ))}
            </div>
          </div>

          <Progress value={progress} className="h-1" />
        </div>
      </div>

      <div className={`flex-1 max-w-6xl mx-auto w-full px-6 py-8 ${showSummaryBar ? "pb-24" : ""}`}>
        {step === "industry" && <IndustrySelectStep onSelect={handleIndustrySelect} />}
        {step === "sales" && <SalesStep selectedKeys={selectedKeys} onToggle={toggle} onRequestMissing={() => setRequestDialogOpen(true)} />}
        {step === "design" && <DesignStep selectedKeys={selectedKeys} onToggle={toggle} onRequestMissing={() => setRequestDialogOpen(true)} activeView={designView} onViewChange={setDesignView} />}
        {step === "approvals" && <ApprovalsStep selectedKeys={selectedKeys} onToggle={toggle} onRequestMissing={() => setRequestDialogOpen(true)} />}
        {step === "site-plan" && <SitePlanStep selectedKeys={selectedKeys} onToggle={toggle} onRequestMissing={() => setRequestDialogOpen(true)} />}
        {step === "floor-electrical" && <FloorElectricalStep selectedKeys={selectedKeys} onToggle={toggle} onRequestMissing={() => setRequestDialogOpen(true)} />}
        {step === "structural-section" && <StructuralSectionStep selectedKeys={selectedKeys} onToggle={toggle} onRequestMissing={() => setRequestDialogOpen(true)} />}
        {step === "architectural" && <ArchitecturalStep selectedKeys={selectedKeys} onToggle={toggle} onRequestMissing={() => setRequestDialogOpen(true)} />}
        {step === "hb-approvals" && <HomebuilderApprovalsStep selectedKeys={selectedKeys} onToggle={toggle} onRequestMissing={() => setRequestDialogOpen(true)} />}
        {step === "summary" && <SummaryStep count={count} onComplete={goNext} industryId={selectedIndustry?.id} selectedLabels={getSelectedLabels()} selectedKeys={selectedKeys} />}
        {step === "invite" && <InviteTeamStep count={count} onComplete={handleComplete} />}
      </div>

      {showSummaryBar && (
        <SelectionSummaryBar
          count={count}
          labels={getSelectedLabels()}
          selectedKeys={selectedKeys}
          onBack={goBack}
          onNext={handleNextClick}
          showBack={stepIndex > 0}
          nextLabel={nextLabel}
        />
      )}

      <IntegrationRequestDialog
        open={requestDialogOpen}
        onOpenChange={setRequestDialogOpen}
        onConfirm={() => setRequestDialogOpen(false)}
        title="Request New Data Points"
        description="Tell us what data you need that isn't listed. You can describe multiple data points — we'll work to add them to your dataset."
      />
    </div>
  );
}
